import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Save } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { purchaseService } from '../../services/purchaseService';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import Field from '../../components/Field';
import PickerPopover from '../../components/PickerPopover';
import { useColumnFilters, FilterPopover } from '../../components/gridFilters';
import { cell, GRID } from '../../styles/gridStyles';
import { LANDED_COST_STAGES, ALL_COMPONENTS, VAT_COMPONENT } from '../../config/landedCostStages';
import {
  componentValue, componentValueBase, stageTotalBase, landedTotalBase, currencyOfComponent,
} from '../../utils/landedCost';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { userPrefs, PREFS } from '../../utils/userPreferences';

// Year is no longer surfaced in the UI. Kept internally because the current-cost
// column and item universe come from act_stock_codes (Stock Actuals), which is
// still year-based. Drops out entirely once financial_year leaves those tables.
const THIS_YEAR = new Date().getFullYear();

const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const stageOfComponent = (code) =>
  LANDED_COST_STAGES.find((s) => s.components.some((c) => c.code === code));

// Variance cell: Landed - Current. Up/red = cost increased, down/green = lower.
const varianceCell = (v, { idx = 0, total = false } = {}) => {
  const up = v > 0.005;
  const down = v < -0.005;
  const color = up ? '#ef4444' : down ? '#22c55e' : (total ? GRID.color.totalText : GRID.color.muted);
  const zebra = idx % 2 === 0 ? GRID.color.zebraEven : GRID.color.zebraOdd;
  return {
    type: 'text',
    text: `${up ? '▲ ' : down ? '▼ ' : ''}${fmt(Math.abs(v))}`,
    nonEditable: true,
    style: {
      background: total ? GRID.color.totalBg : zebra,
      color,
      fontFamily: GRID.numFont,
      fontSize: GRID.size.fontData,
      fontWeight: total ? '700' : '600',
      justifyContent: 'flex-end',
      paddingRight: 10,
      ...(total ? { border: { top: { color: GRID.color.totalBg, style: 'solid', width: '2px' } } } : {}),
    },
  };
};

const StockedPricing = () => {
  const { selectedSite, selectedVersion, vatRate, fyStartMonth } = useAppContext();

  // Warehouse / product class / months are all remembered per user (see
  // utils/userPreferences.js). Warehouse can't be seeded here — it is only valid
  // for the current site, so it is restored once that site's list has loaded.
  const [warehouses, setWarehouses] = useState([]);
  const [warehouse, setWarehouse] = useState('');
  const [productClasses, setProductClasses] = useState([]);
  const [productClass, setProductClass] = useState(() => userPrefs.get(PREFS.PURCHASE_PRODUCT_CLASS, ''));
  const year = THIS_YEAR; // internal only — no Year selector in the UI
  const [selectedPeriods, setSelectedPeriods] = useState(() => {
    const saved = userPrefs.get(PREFS.STOCKED_PERIODS, null);
    const valid = (Array.isArray(saved) ? saved : []).filter((p) => PERIODS.includes(p));
    return valid.length ? valid.sort((a, b) => a - b) : [1];
  });

  // The grid shows/edits the lowest selected month; saving fans out to all.
  const primaryPeriod = selectedPeriods.length ? Math.min(...selectedPeriods) : null;

  const [items, setItems] = useState([]);
  const [currencies, setCurrencies] = useState([]); // md_currencies: code, rate, base, color
  const [suppliers, setSuppliers] = useState([]);   // md_suppliers (active only)
  // Which item's Supplier cell has the picker open — { stock_code, x, y }.
  const [supplierPicker, setSupplierPicker] = useState(null);
  // Stock codes whose supplier was changed but not yet saved. Kept apart from
  // `dirty` (which is keyed by price cell) because a supplier is not a cell.
  const [dirtySuppliers, setDirtySuppliers] = useState({});
  // Stage view state is remembered per user (see utils/userPreferences.js).
  const [collapsed, setCollapsed] = useState(() =>
    userPrefs.get(
      PREFS.STOCKED_STAGE_COLLAPSED,
      Object.fromEntries(LANDED_COST_STAGES.map((s) => [s.code, true]))
    )
  );
  const [hiddenStages, setHiddenStages] = useState(() =>
    userPrefs.get(PREFS.STOCKED_HIDDEN_STAGES, {}) // { stageCode: true } -> hidden
  );
  const [dirty, setDirty] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [ccyMenuOpen, setCcyMenuOpen] = useState(false); // "Change currency" dropdown

  // Persist the stage view and the pickers whenever they change.
  useEffect(() => { userPrefs.set(PREFS.STOCKED_STAGE_COLLAPSED, collapsed); }, [collapsed]);
  useEffect(() => { userPrefs.set(PREFS.STOCKED_HIDDEN_STAGES, hiddenStages); }, [hiddenStages]);
  useEffect(() => { userPrefs.set(PREFS.STOCKED_PERIODS, selectedPeriods); }, [selectedPeriods]);
  useEffect(() => { userPrefs.set(PREFS.PURCHASE_PRODUCT_CLASS, productClass); }, [productClass]);
  useEffect(() => {
    if (!selectedSite || !warehouse) return; // '' = nothing chosen yet, not a choice
    userPrefs.set(PREFS.PURCHASE_WAREHOUSE, {
      ...(userPrefs.get(PREFS.PURCHASE_WAREHOUSE, {}) || {}), [selectedSite]: warehouse,
    });
  }, [selectedSite, warehouse]);

  // ---- Load pickers --------------------------------------------------------
  useEffect(() => {
    purchaseService.getProductClasses().then((r) => {
      if (r.success) setProductClasses(r.data);
    }).catch(() => {});
  }, []);

  // A remembered product class that no longer exists would leave the select
  // blank and silently filter everything out — drop back to All classes.
  useEffect(() => {
    if (productClass && productClasses.length && !productClasses.includes(productClass)) {
      setProductClass('');
    }
  }, [productClasses, productClass]);

  // Currencies are per budget version — reload when the active version changes
  // (the version travels in the x-budget-version-id header on the request).
  useEffect(() => {
    purchaseService.getCurrencies().then((r) => {
      if (r.success) setCurrencies(r.data);
    }).catch(() => {});
  }, [selectedVersion]);

  // Suppliers are master data, not per version — loaded once.
  useEffect(() => {
    purchaseService.getSuppliers().then((r) => {
      if (r.success) setSuppliers(r.data);
    }).catch(() => {});
  }, []);

  const supplierName = useCallback(
    (code) => suppliers.find((s) => s.supplier_code === code)?.supplier_name || '',
    [suppliers]
  );

  // ---- Currency helpers (per cell) -----------------------------------------
  // Currency lives per component cell: an item can be imported (USD) on the
  // base price while its transport legs sit in the base currency.
  const baseCode = useMemo(
    () => currencies.find((c) => c.is_base)?.currency_code || null,
    [currencies]
  );
  const rateOf = useCallback(
    (code) => {
      const c = currencies.find((x) => x.currency_code === code);
      return c ? Number(c.conversion_to_base) : 1;
    },
    [currencies]
  );
  const colorOf = useCallback(
    (code) => currencies.find((x) => x.currency_code === code)?.display_color || null,
    [currencies]
  );
  // The pricing environment every landed-cost formula runs against. An item row
  // is already the util's "cell" shape ({ components, currencies }), so it can be
  // handed straight to componentValue / stageTotalBase / landedTotalBase.
  const ctx = useMemo(() => ({ vatRate, rateOf, baseCode }), [vatRate, rateOf, baseCode]);

  // Colour accent for a non-base cell — display only, no part of the math.
  const cellAccent = useCallback(
    (item, code) => {
      const ccy = currencyOfComponent(item, code, ctx);
      return ccy && ccy !== baseCode ? colorOf(ccy) : undefined;
    },
    [ctx, baseCode, colorOf]
  );

  // Clearing the warehouse first matters: it stops the grid loading the OLD
  // site's warehouse against the new site while the list is in flight. The
  // remembered warehouse is then re-applied only if this site still has it.
  useEffect(() => {
    setWarehouse('');
    setItems([]);
    if (!selectedSite) { setWarehouses([]); return undefined; }
    let cancelled = false;
    purchaseService.getWarehouses(selectedSite).then((r) => {
      if (cancelled) return;
      const list = r.success ? r.data : [];
      setWarehouses(list);
      const saved = (userPrefs.get(PREFS.PURCHASE_WAREHOUSE, {}) || {})[selectedSite];
      if (saved && list.some((w) => String(w.warehouse_id) === String(saved))) setWarehouse(saved);
    }).catch(() => { if (!cancelled) setWarehouses([]); });
    return () => { cancelled = true; };
  }, [selectedSite]);

  // ---- Load grid data ------------------------------------------------------
  const loadItems = useCallback(async () => {
    if (!warehouse || !primaryPeriod) { setItems([]); return; }
    setIsLoading(true);
    try {
      const r = await purchaseService.getStocked({
        warehouse, year, period: primaryPeriod, productClass: productClass || undefined,
      });
      setItems(r.success ? r.data.items : []);
      setDirty({});
      setDirtySuppliers({});
      setSupplierPicker(null);
    } catch (err) {
      console.error('Failed to load stocked pricing:', err.response?.data || err.message);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [warehouse, year, primaryPeriod, productClass]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ---- Calculations --------------------------------------------------------
  // All of them live in utils/landedCost.js — VAT is auto-calculated there from
  // the customs value, and every cross-component total is converted to base
  // currency by each cell's own rate. Monthly Pricing inverts the same formulas.

  // Which columns are visible right now (collapsed stage -> 1 total column).
  const visibleCols = useMemo(() => {
    const cols = [];
    for (const stage of LANDED_COST_STAGES) {
      if (hiddenStages[stage.code]) continue; // hidden via the Columns menu
      if (collapsed[stage.code]) cols.push({ kind: 'stage', stage });
      else stage.components.forEach((comp, i) => cols.push({ kind: 'comp', stage, comp, first: i === 0 }));
    }
    return cols;
  }, [collapsed, hiddenStages]);

  // ---- Stock Code column filter (header ▾) ---------------------------------
  // Filter on the bare stock code so the value checklist is a clean list of
  // codes rather than "code · description" strings.
  const accessors = useMemo(() => ({
    item: (it) => it.stock_code || '',
    // "Show me everything I buy from this supplier" is the question this column
    // exists to answer, so it filters like the stock code does.
    supplier: (it) => it.supplier_code || '',
  }), []);
  const { filters, setFilter, clearAll, filtered, activeIds } = useColumnFilters(items, accessors);
  const [filterUI, setFilterUI] = useState(null); // { colId, x, y, options }

  const onHeaderMenu = useCallback((columnId, bounds) => {
    const acc = accessors[columnId];
    if (!acc) return;
    const options = [...new Set(items.map((it) => String(acc(it) ?? '')))].sort();
    setFilterUI({
      colId: columnId,
      options,
      x: bounds ? bounds.x : 200,
      y: bounds ? bounds.y + bounds.height : 200,
    });
  }, [accessors, items]);

  const columns = useMemo(() => ([
    { columnId: 'item', width: 280, resizable: true, hasMenu: true },
    { columnId: 'uom', width: 72, resizable: true },
    { columnId: 'supplier', width: 190, resizable: true, hasMenu: true },
    ...visibleCols.map((vc) =>
      vc.kind === 'stage'
        ? { columnId: `STG:${vc.stage.code}`, width: 130, resizable: true }
        : { columnId: `CMP:${vc.comp.code}`, width: 145, resizable: true }
    ),
    { columnId: 'CURRENT', width: 130, resizable: true },
    { columnId: 'GRAND', width: 150, resizable: true },
    { columnId: 'VAR', width: 120, resizable: true },
  ]), [visibleCols]);

  const gridRows = useMemo(() => {
    const headerCells = [
      cell.header(activeIds.includes('item') ? 'Item / Stock Code ●' : 'Item / Stock Code'),
      cell.header('UoM'),
      cell.header(activeIds.includes('supplier') ? 'Supplier ●' : 'Supplier'),
      ...visibleCols.map((vc) => {
        if (vc.kind === 'stage') return cell.drillHeader(`▸ ${vc.stage.short}`, 'quarter');
        const label = vc.comp.isVat ? `${vc.comp.label} (${vatRate}%)` : vc.comp.label;
        // First component of an expanded stage carries the collapse chevron; the
        // stage name is dropped here so the column shows just the component name.
        if (vc.first) return cell.drillHeader(`▾ ${label}`, 'year');
        return cell.header(label, { align: 'right' });
      }),
      cell.header('Current Cost', { align: 'right' }),
      cell.header(`Landed Total${baseCode ? ` (${baseCode})` : ''}`, { align: 'right' }),
      cell.header('Variance', { align: 'right' }),
    ];
    const headerRow = { rowId: 'header', height: GRID.size.headerRow, cells: headerCells };

    const dataRows = filtered.map((item, idx) => ({
      rowId: item.stock_code,
      height: GRID.size.dataRow,
      cells: [
        cell.label(`${item.stock_code} · ${item.description}`, { idx }),
        cell.text(item.unit_of_measure || '—', { idx }),
        // Read-only: a click opens the supplier picker rather than a text
        // editor, so nobody can type a code that is not in md_suppliers.
        cell.text(
          item.supplier_code
            ? `${item.supplier_code}${supplierName(item.supplier_code) ? ` · ${supplierName(item.supplier_code)}` : ''}`
            : '— set supplier —',
          { idx }
        ),
        ...visibleCols.map((vc) =>
          vc.kind === 'stage'
            ? cell.aggregate(stageTotalBase(item, vc.stage, ctx), { kind: 'quarter' })
            : cell.number(componentValue(item, vc.comp.code, ctx), {
                editable: vc.comp.code !== VAT_COMPONENT,
                idx,
                accent: cellAccent(item, vc.comp.code),
              })
        ),
        cell.number(item.cost_amount, { editable: false, idx }),
        cell.aggregate(landedTotalBase(item, ctx), { kind: 'year' }),
        varianceCell(landedTotalBase(item, ctx) - Number(item.cost_amount || 0), { idx }),
      ],
    }));

    // Totals follow what's on screen (the filtered rows).
    const totalCells = [
      cell.totalLabel(activeIds.length ? 'TOTAL (filtered)' : 'TOTAL'),
      cell.totalBlank(),
      cell.totalBlank(),
      ...visibleCols.map((vc) => {
        // Column totals mix currencies across cells, so convert each to base.
        const sum = filtered.reduce(
          (a, item) => a + (vc.kind === 'stage'
            ? stageTotalBase(item, vc.stage, ctx)
            : componentValueBase(item, vc.comp.code, ctx)),
          0
        );
        return cell.totalNumber(sum);
      }),
      cell.totalNumber(filtered.reduce((a, item) => a + Number(item.cost_amount || 0), 0)),
      cell.totalNumber(filtered.reduce((a, item) => a + landedTotalBase(item, ctx), 0)),
      varianceCell(
        filtered.reduce((a, item) => a + landedTotalBase(item, ctx) - Number(item.cost_amount || 0), 0),
        { total: true }
      ),
    ];
    const totalRow = { rowId: 'totals', height: GRID.size.totalRow, cells: totalCells };

    return [headerRow, ...dataRows, totalRow];
  }, [filtered, visibleCols, ctx, cellAccent, vatRate, baseCode, activeIds, supplierName]);

  // ---- Currency: change via toolbar button on the selected cells -----------
  // ReactGrid's live selection (ranges) and focused cell are tracked into refs
  // by the grid's onSelectionChanged / onFocusLocationChanged handlers, so the
  // "Change currency" toolbar button knows which cells to act on.
  const selectionRef = useRef([]);   // Range[] from onSelectionChanged
  const focusRef = useRef(null);     // CellLocation from onFocusLocationChanged
  const ccyMenuRef = useRef(null);   // wrapper for the "Change currency" dropdown

  // Close the currency dropdown when clicking outside it.
  useEffect(() => {
    if (!ccyMenuOpen) return undefined;
    const onDown = (e) => {
      if (ccyMenuRef.current && !ccyMenuRef.current.contains(e.target)) setCcyMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ccyMenuOpen]);

  // Map a column id to the component(s) it represents.
  const componentsForColumn = (col) => {
    if (col.startsWith('CMP:')) return [col.slice(4)];
    if (col.startsWith('STG:')) {
      const stage = LANDED_COST_STAGES.find((s) => s.code === col.slice(4));
      return stage ? stage.components.map((cc) => cc.code) : [];
    }
    return null; // not a price column -> "whole item" shortcut
  };

  const dedupeTargets = (targets) => {
    const seen = new Set();
    return targets.filter((t) => {
      if (t.component_code === VAT_COMPONENT) return false; // VAT is always base
      const k = `${t.stock_code}:${t.component_code}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  // Build (stock_code, component_code) targets from the current selection
  // ranges, falling back to the single focused cell.
  const currentTargets = () => {
    const targets = [];
    const wholeRows = new Set();

    const ranges = selectionRef.current || [];
    ranges.forEach((range) => {
      const rowIds = (range.rows || []).map((r) => String(r.rowId)).filter((id) => id !== 'header' && id !== 'totals');
      const colIds = (range.columns || []).map((c) => String(c.columnId));
      rowIds.forEach((rowId) => colIds.forEach((col) => {
        const comps = componentsForColumn(col);
        if (comps === null) wholeRows.add(rowId);
        else comps.forEach((code) => targets.push({ stock_code: rowId, component_code: code }));
      }));
    });

    // Fall back to the focused cell if there is no range selection.
    if (targets.length === 0 && wholeRows.size === 0 && focusRef.current) {
      const loc = focusRef.current;
      const rowId = String(loc.rowId);
      if (rowId !== 'header' && rowId !== 'totals') {
        const comps = componentsForColumn(String(loc.columnId));
        if (comps === null) wholeRows.add(rowId);
        else comps.forEach((code) => targets.push({ stock_code: rowId, component_code: code }));
      }
    }

    wholeRows.forEach((sc) => ALL_COMPONENTS.forEach((cc) => targets.push({ stock_code: sc, component_code: cc.code })));
    return dedupeTargets(targets);
  };

  const applyCurrency = (targets, code) => {
    const byStock = new Map(); // stock_code -> Set(component_code)
    for (const t of targets) {
      if (!byStock.has(t.stock_code)) byStock.set(t.stock_code, new Set());
      byStock.get(t.stock_code).add(t.component_code);
    }
    setItems((prev) => prev.map((it) => {
      const comps = byStock.get(it.stock_code);
      if (!comps) return it;
      const nextCurrencies = { ...(it.currencies || {}) };
      comps.forEach((compCode) => { nextCurrencies[compCode] = code; });
      return { ...it, currencies: nextCurrencies };
    }));
    // Mark those cells dirty so Save lights up and rewrites them with the currency.
    setDirty((prev) => {
      const d = { ...prev };
      for (const t of targets) d[`${t.stock_code}:${t.component_code}`] = true;
      return d;
    });
  };

  const applyCurrencyToSelection = (code) => {
    setCcyMenuOpen(false);
    const targets = currentTargets();
    if (targets.length === 0) {
      alert('Select the price cell(s) you want to change, then pick a currency.');
      return;
    }
    applyCurrency(targets, code);
  };

  // ---- Supplier (per item, not per component) ------------------------------
  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({
      code: s.supplier_code,
      description: s.supplier_name,
      hint: s.currency_code || '',
    })),
    [suppliers]
  );

  const applySupplier = (stockCode, supplierCode) => {
    setItems((prev) => prev.map((it) =>
      (it.stock_code === stockCode ? { ...it, supplier_code: supplierCode } : it)));
    setDirtySuppliers((prev) => ({ ...prev, [stockCode]: true }));
    setSupplierPicker(null);
  };

  // ---- Interactions --------------------------------------------------------
  const handleFocus = (loc) => {
    // Supplier is chosen from a picker anchored under the cell — the cell is
    // read-only, so a code that is not in md_suppliers cannot be typed in.
    if (String(loc.columnId) === 'supplier' && loc.rowId !== 'header' && loc.rowId !== 'totals') {
      const b = loc.bounds;
      setSupplierPicker({
        stock_code: String(loc.rowId),
        x: b ? b.x : 200,
        y: b ? b.y + b.height : 200,
      });
      return false;
    }
    if (loc.rowId !== 'header') return true;
    const col = String(loc.columnId);
    if (col.startsWith('STG:')) { setCollapsed((p) => ({ ...p, [col.slice(4)]: false })); return false; }
    if (col.startsWith('CMP:')) {
      const stage = stageOfComponent(col.slice(4));
      if (stage && stage.components[0].code === col.slice(4)) {
        setCollapsed((p) => ({ ...p, [stage.code]: true }));
        return false;
      }
    }
    return true;
  };

  const handleChanges = (changes) => {
    setItems((prev) => {
      const next = prev.map((it) => ({ ...it, components: { ...it.components } }));
      // A multi-cell paste arrives as one onCellsChanged call PER CELL in a
      // single tick. Collect this call's keys locally and merge them with a
      // functional setDirty — seeding from the `dirty` closure instead would
      // restart from the same stale base each call and only the last pasted
      // cell would survive to be saved.
      const touched = {};
      for (const ch of changes) {
        const col = String(ch.columnId);
        if (!col.startsWith('CMP:') || ch.rowId === 'header' || ch.rowId === 'totals') continue;
        const code = col.slice(4);
        if (code === VAT_COMPONENT) continue; // VAT is auto-calculated
        const val = Number(ch.newCell?.value);
        if (isNaN(val)) continue;
        const item = next.find((i) => i.stock_code === ch.rowId);
        if (!item) continue;
        item.components[code] = val;
        touched[`${ch.rowId}:${code}`] = true;
      }
      setDirty((prevDirty) => ({ ...prevDirty, ...touched }));
      return next;
    });
  };

  const handleSave = async () => {
    // Send every priced cell (non-zero) plus anything explicitly edited, so the
    // displayed build-up is written across ALL selected months.
    const changes = [];
    for (const item of items) {
      for (const c of ALL_COMPONENTS) {
        const val = componentValue(item, c.code, ctx);
        if (val !== 0 || dirty[`${item.stock_code}:${c.code}`]) {
          changes.push({
            stock_code: item.stock_code,
            component_code: c.code,
            amount: val,
            currency: currencyOfComponent(item, c.code, ctx), // per-cell (null-safe -> base)
          });
        }
      }
    }
    // Supplier goes for every item that HAS one, not just the ones edited —
    // same reasoning as the price cells above. The column is repeated across an
    // item's component rows, and re-stamping it each save is what stops rows
    // inserted by an earlier save from drifting out of step. Items whose
    // supplier was explicitly cleared are sent as null so the clear sticks.
    const supplierPayload = items
      .filter((it) => it.supplier_code || dirtySuppliers[it.stock_code])
      .map((it) => ({ stock_code: it.stock_code, supplier_code: it.supplier_code || null }));

    // A supplier is stored ON the item's pricing rows, so an item that has no
    // priced component yet has nowhere to keep it. Seed a base_price of 0 —
    // which is what the grid is already showing — so choosing a supplier before
    // any price is known still saves instead of quietly doing nothing.
    const priced = new Set(changes.map((c) => c.stock_code));
    for (const s of supplierPayload) {
      if (!priced.has(s.stock_code)) {
        changes.push({ stock_code: s.stock_code, component_code: 'base_price', amount: 0, currency: baseCode });
      }
    }

    if (changes.length === 0 || selectedPeriods.length === 0) return;
    setIsSaving(true);
    try {
      await purchaseService.saveStocked({
        warehouse_id: warehouse, financial_year: year, financial_periods: selectedPeriods,
        changes,
        suppliers: supplierPayload,
      });
      await loadItems(); // reload budget cells (actuals are untouched)
    } catch (err) {
      console.error('Failed to save pricing:', err.response?.data || err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const collapseAll = () => setCollapsed(Object.fromEntries(LANDED_COST_STAGES.map((s) => [s.code, true])));
  const expandAll = () => setCollapsed({});

  const togglePeriod = (p) =>
    setSelectedPeriods((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p].sort((a, b) => a - b)
    );

  const toggleStageHidden = (code) =>
    setHiddenStages((prev) => ({ ...prev, [code]: !prev[code] }));

  const hasChanges = Object.keys(dirty).length > 0 || Object.keys(dirtySuppliers).length > 0;

  const selectClass =
    'text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 px-2 py-1 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50';

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* Header + filters */}
      <div className="shrink-0 px-6 pt-4 pb-3">
        {/* Title sits IN the filter row rather than above it — this header is
            pure chrome, so every row it costs is a row taken off the grid. */}
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <h1
            className="m-0 mr-1 text-lg font-bold leading-7 tracking-tight text-slate-900 dark:text-slate-100"
            title="Build the landed cost of procurement (stocked) items per warehouse and period."
          >
            Stocked Pricing
          </h1>
          <Field dense label="Warehouse">
            <select className={selectClass} value={warehouse} disabled={!selectedSite || warehouses.length === 0}
              onChange={(e) => setWarehouse(e.target.value)}>
              <option value="">{selectedSite ? 'Select warehouse' : 'Select a site first'}</option>
              {warehouses.map((w) => <option key={w.warehouse_id} value={w.warehouse_id}>{w.description}</option>)}
            </select>
          </Field>
          <Field dense label="Product Class">
            <select className={selectClass} value={productClass} onChange={(e) => setProductClass(e.target.value)}>
              <option value="">All classes</option>
              {productClasses.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          {/* The old "Editing <month>. Saving writes to N months." note lived
              here; the grid hint and the "Save to N months" button already say
              it, so it was two lines of duplicate prose. */}
          <Field dense label="Months" hint="Prices apply to these months">
            <div className="flex flex-wrap items-center gap-1">
              {PERIODS.map((p) => {
                const on = selectedPeriods.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePeriod(p)}
                    title={p === primaryPeriod ? `${periodLabel(p, fyStartMonth)} — the month shown in the grid` : undefined}
                    className={`px-1.5 py-0.5 text-[11px] rounded border transition-colors ${
                      on
                        ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    {periodLabel(p, fyStartMonth)}
                  </button>
                );
              })}
              <button type="button" onClick={() => setSelectedPeriods([...PERIODS])}
                className="ml-1 text-[11px] text-slate-500 dark:text-slate-400 hover:underline">All</button>
              <button type="button" onClick={() => setSelectedPeriods([])}
                className="text-[11px] text-slate-500 dark:text-slate-400 hover:underline">None</button>
            </div>
          </Field>
          <Field dense label="Stages" hint="Show or hide a stage of the build-up">
            <div className="flex flex-wrap items-center gap-1">
              {LANDED_COST_STAGES.map((s) => {
                const shown = !hiddenStages[s.code];
                return (
                  <button
                    key={s.code}
                    type="button"
                    onClick={() => toggleStageHidden(s.code)}
                    title={shown ? `Hide ${s.label}` : `Show ${s.label}`}
                    className={`px-1.5 py-0.5 text-[11px] rounded border transition-colors ${
                      shown
                        ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
                        : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-300 dark:border-slate-600 line-through'
                    }`}
                  >
                    {s.short}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 px-6 pb-4">
        <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!warehouse ? (
            <div className="p-10 text-center text-sm text-slate-400">
              {selectedSite
                ? 'Select a warehouse to begin pricing.'
                : 'Select a site in the top bar, then choose a warehouse.'}
            </div>
          ) : !primaryPeriod ? (
            <div className="p-10 text-center text-sm text-slate-400">
              Select at least one month to price.
            </div>
          ) : (
            <MantleGrid
              rows={gridRows}
              columns={columns}
              isLoading={isLoading}
              onCellsChanged={handleChanges}
              onFocusLocationChanging={handleFocus}
              onHeaderMenuClick={onHeaderMenu}
              gridProps={{
                onSelectionChanged: (ranges) => { selectionRef.current = ranges || []; },
                onFocusLocationChanged: (loc) => { focusRef.current = loc || null; },
              }}
              hint={`Editing ${periodLabel(primaryPeriod, fyStartMonth)} · cell amounts in their own currency (coloured left border = non-base), totals in ${baseCode || 'base'} · select cells then "Change currency" · click a Supplier cell to choose who the item is bought from.`}
              toolbar={
                <>
                  {activeIds.length > 0 && (
                    <MantleGridButton onClick={clearAll} accent>
                      Clear filters ({activeIds.length})
                    </MantleGridButton>
                  )}
                  <MantleGridButton onClick={expandAll}>Expand all</MantleGridButton>
                  <MantleGridButton onClick={collapseAll}>Collapse all</MantleGridButton>

                  {/* Change currency: acts on the currently selected cell(s). */}
                  <div ref={ccyMenuRef} style={{ position: 'relative' }}>
                    <MantleGridButton onClick={() => setCcyMenuOpen((o) => !o)}>Change currency ▾</MantleGridButton>
                    {ccyMenuOpen && (
                      <div
                        style={{
                          position: 'absolute', top: '100%', right: 0, zIndex: 30, marginTop: 4,
                          minWidth: 220, padding: 4, borderRadius: 8,
                          background: 'var(--grid-readonly-bg, #ffffff)',
                          border: '1px solid var(--grid-border-strong, #cbd5e1)',
                          boxShadow: '0 10px 24px rgba(15,23,42,0.18)',
                        }}
                      >
                        <div style={{ padding: '6px 10px', fontSize: 11, fontWeight: 700, color: 'var(--grid-subtext, #64748b)' }}>
                          Apply to selected cells
                        </div>
                        {currencies.length === 0 && (
                          <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--grid-muted, #94a3b8)' }}>
                            No currencies configured (Budget Setup → Currencies).
                          </div>
                        )}
                        {currencies.map((cur) => (
                          <button
                            key={cur.currency_code}
                            onClick={() => applyCurrencyToSelection(cur.currency_code)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                              padding: '6px 10px', fontSize: 12, border: 'none', background: 'transparent',
                              color: 'var(--grid-text, #0f172a)', cursor: 'pointer', borderRadius: 6,
                            }}
                          >
                            <span style={{
                              width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                              background: cur.display_color || '#E0E0E0', border: '1px solid rgba(0,0,0,0.12)',
                            }} />
                            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{cur.currency_code}</span>
                            <span style={{ color: 'var(--grid-subtext, #64748b)' }}>
                              {cur.currency_name}{cur.is_base ? ' (base)' : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white disabled:opacity-50 shadow-sm transition-colors ${
                      hasChanges
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-slate-400 dark:bg-slate-600 hover:bg-slate-500 dark:hover:bg-slate-500'
                    }`}
                  >
                    <Save className="w-3.5 h-3.5" />
                    {isSaving
                      ? 'Saving…'
                      : `Save to ${selectedPeriods.length} month${selectedPeriods.length === 1 ? '' : 's'}`}
                  </button>
                </>
              }
            />
          )}
        </div>
      </div>

      {/* Supplier picker — anchored under the Supplier cell that was clicked. */}
      {supplierPicker && (
        <PickerPopover
          anchor={supplierPicker}
          options={supplierOptions}
          value={items.find((it) => it.stock_code === supplierPicker.stock_code)?.supplier_code}
          onPick={(code) => applySupplier(supplierPicker.stock_code, code)}
          onClear={() => applySupplier(supplierPicker.stock_code, null)}
          clearLabel="No supplier"
          placeholder="Filter suppliers…"
          emptyText={suppliers.length === 0 ? 'No suppliers yet — add them in Purchase > Suppliers.' : 'No match.'}
          onClose={() => setSupplierPicker(null)}
        />
      )}

      {/* Stock Code column filter — opened from the header ▾ menu. */}
      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}
    </div>
  );
};

export default StockedPricing;
